import {DateTime} from "luxon";

export const STAGES = ["Prearranged","Waiting for photos/information","Designing","Awaiting approval","Approved","Ordered from Signs365","Complete"];
export const SEASONS = ["Fall","Winter","Spring"];
export const productionStage = stage => ["Shipped","Delivered"].includes(stage) ? "Ordered from Signs365" : stage;
export const today = () => DateTime.now().setZone("America/New_York").toISODate();
export function productionDayCount(value,referenceDate=today()) {
  const zone="America/New_York";
  try {
    const raw=typeof value?.toDate==="function" ? value.toDate() : value;
    const shoot=raw instanceof Date ? DateTime.fromJSDate(raw,{zone}) : typeof raw==="string" ? DateTime.fromISO(raw,{zone}) : null;
    const current=DateTime.fromISO(referenceDate,{zone});
    if(!shoot?.isValid || !current.isValid)return null;
    const elapsed=Math.round(current.startOf("day").diff(shoot.startOf("day"),"days").days);
    return {days:Math.max(0,elapsed),startsIn:Math.max(0,-elapsed),shootDate:shoot.toISODate()};
  }catch{return null;}
}
export const money = cents => new Intl.NumberFormat("en-US",{style:"currency",currency:"USD"}).format(cents/100);
export const normalize = value => String(value || "").normalize("NFKC").trim().toLowerCase().replace(/\s+/g," ");
export function previousDesignJobs(job,jobs) {
  if(!job.clientId || !normalize(job.team))return [];
  return jobs.filter(source=>!source.deletedAt && source.id!==job.id && source.clientId===job.clientId && normalize(source.team)===normalize(job.team) && Number(source.year)<Number(job.year))
    .sort((a,b)=>Number(b.year)-Number(a.year) || Number(normalize(b.season)===normalize(job.season))-Number(normalize(a.season)===normalize(job.season)) || String(a.season).localeCompare(String(b.season)) || String(a.id).localeCompare(String(b.id)));
}
export const JOB_NEED_TYPES={senior:"Senior banners",team:"Team banner",roster:"Roster poster",miscellaneous:"Miscellaneous"};
export const SENIOR_SIZES=["2x3","2x4","3x5"];
export function jobNeedsDraft(needs={}) {
  const senior=needs.senior || "";
  return {senior:Boolean(senior),seniorSize:SENIOR_SIZES.includes(senior) ? senior : senior ? "custom" : "2x3",seniorCustomSize:SENIOR_SIZES.includes(senior) ? "" : senior,team:Boolean(needs.team),teamSize:needs.team || "",roster:Boolean(needs.roster),rosterSize:needs.roster || "",miscellaneous:Boolean(needs.miscellaneous),miscellaneousTitle:needs.miscellaneous || ""};
}
export function cleanJobNeeds(draft) {
  const needs={};
  for(const [type,label] of Object.entries(JOB_NEED_TYPES)) {
    if(!draft[type])continue;
    if(type==="miscellaneous"){
      const title=String(draft.miscellaneousTitle || "").trim();
      if(!title || title.length>200)throw new Error("Enter a miscellaneous item title (up to 200 characters).");
      needs.miscellaneous=title;continue;
    }
    const size=String(type==="senior" ? draft.seniorSize==="custom" ? draft.seniorCustomSize : draft.seniorSize : draft[type+"Size"]).trim();
    if(!size || size==="undefined" || size.length>100)throw new Error("Enter a size for "+label.toLowerCase()+" (up to 100 characters).");
    if(type==="senior" && draft.seniorSize!=="custom" && !SENIOR_SIZES.includes(size))throw new Error("Choose a senior banner size or Custom.");
    needs[type]=size;
  }
  return needs;
}
export function plannedJobNeeds(job) {
  return job.needs===undefined ? null : Object.entries(JOB_NEED_TYPES).filter(([type])=>Boolean(job.needs[type])).map(([type,label])=>({type,label,...(type==="miscellaneous" ? {title:job.needs[type]} : {size:job.needs[type]})}));
}
export function bannerNeeds(items) {
  const groups=new Map();
  items.forEach(item=>{
    const kind=item.kind==="team" ? "team" : "senior";
    const raw=normalize(item.size),size=raw.replace(/(\d)\s*[x×]\s*(?=\d)/g,"$1x");
    const key=JSON.stringify([kind,size]);
    if(!groups.has(key))groups.set(key,{kind,size:size || "Size not specified",quantity:0});
    groups.get(key).quantity+=Number(item.quantity) || 1;
  });
  return [...groups.values()].sort((a,b)=>a.kind.localeCompare(b.kind)||a.size.localeCompare(b.size,undefined,{numeric:true}));
}
export function selectedDesignSummary(job,designs) {
  const design=designs.find(row=>row.id===job.selectedDesignId);
  const selected=Boolean(design || job.selectedDesignId || String(job.templateNumber || "").trim() || job.basis==="previous");
  return {design,status:design?.thumbnailPath ? "Selected design" : selected ? "Design selected — no preview uploaded" : "Waiting on design selection"};
}
export function bannerDesignStatus(job) {
  return job.basis==="previous" ? "Use last year's design" : job.basis==="template" ? "Template selected"+(job.templateNumber ? " — Design #"+job.templateNumber : "") : "No design selected";
}
export function designSelection(job) {
  return Object.fromEntries(["basis","templateNumber","previousJobId","previousDesignId"].map(key=>[key,job[key] || ""]));
}
export function approvalMatchesDesign(job,approval) {
  if(approval.designId!==(job.selectedDesignId || ""))return false;
  if(!approval.selection)return Boolean(job.selectedDesignId);
  return job.basis!=="none" && Object.entries(designSelection(job)).every(([key,value])=>approval.selection[key]===value);
}
export function cents(value) {
  const text=String(value).trim().replace(/^\$/," ").trim();
  if(!/^\d+(\.\d{1,2})?$/.test(text))throw new Error("Enter a nonnegative price with at most two decimal places.");
  const result=Math.round(Number(text)*100);
  if(!Number.isSafeInteger(result) || result>100000000)throw new Error("Amount is too large.");
  return result;
}
export function validDate(value, required=false) {
  if(!value && !required)return "";
  if(!/^\d{4}-\d{2}-\d{2}$/.test(value) || !DateTime.fromISO(value).isValid)throw new Error("Enter a valid date.");
  return value;
}
export function cleanOrderDetails(number,date,shippedDate="",finalSales="") {
  const vendorOrderNumber=String(number || "").trim();
  if(!vendorOrderNumber || vendorOrderNumber.length>2000)throw new Error("Enter the Signs365 order number.");
  validDate(date,true);if(date>today())throw new Error("Order date cannot be in the future.");
  if(shippedDate){validDate(shippedDate,true);if(shippedDate>today())throw new Error("Ship date cannot be in the future.");if(shippedDate<date)throw new Error("Ship date cannot be before the order date.");}
  if(shippedDate && !String(finalSales).trim())throw new Error("Enter the final sales total before completing this job.");
  return {stage:shippedDate ? "Complete" : "Ordered from Signs365",vendorOrderNumber,orderDate:date,shippedDate,...(String(finalSales).trim() ? {finalSalesCents:cents(finalSales)} : {})};
}
export function itemKey(item) {
  return [item.kind,normalize(item.name),normalize(item.jersey),normalize(item.orderId)].join("|");
}
export function itemFlags(item) {
  const flags=[];
  if(!item.name?.trim())flags.push(item.kind==="senior" ? "Missing player name" : "Missing team banner label");
  if(!item.size?.trim())flags.push("Missing banner size");
  if(item.kind==="senior" && !item.photoReference?.trim())flags.push("Missing photo reference");
  if(item.priceCents===null || item.priceCents===undefined)flags.push("Missing price");
  if(!item.payer?.trim())flags.push("Missing payer responsibility");
  return flags;
}
export function cleanItem(input) {
  const kind=input.kind==="team" ? "team" : "senior";
  const quantity=Number(input.quantity===undefined || input.quantity==="" ? 1 : input.quantity);
  if(!Number.isInteger(quantity) || quantity<1 || quantity>1000)throw new Error("Quantity must be a whole number from 1 to 1,000.");
  const result={kind,quantity,priceCents:input.price==="" || input.price===undefined ? null : cents(input.price)};
  ["name","jersey","graduationYear","size","photoReference","payer","orderId","notes"].forEach(key=>{result[key]=String(input[key] || "").trim();if(result[key].length>1000)throw new Error("Item fields must be 1,000 characters or fewer.");});
  if(result.name.length>200 || result.jersey.length>40 || result.orderId.length>100)throw new Error("Use a name up to 200, jersey up to 40, and order number up to 100 characters.");
  return result;
}
export function billingTotals(items,payers,payments) {
  payments=payments.filter(payment=>!payment.voided);
  const total=items.reduce((sum,item)=>sum+(item.priceCents || 0)*item.quantity,0);
  const allocated=payers.reduce((sum,payer)=>sum+payer.responsibilityCents,0);
  const received=payments.reduce((sum,payment)=>sum+payment.amountCents,0);
  return {total,allocated,received,balance:total-received,unallocated:total-allocated,missingPrices:items.filter(item=>item.priceCents==null).length,
    payers:payers.map(payer=>({...payer,received:payments.filter(payment=>payment.payerId===payer.id).reduce((sum,payment)=>sum+payment.amountCents,0)}))};
}
export function requireStage(stage,job,approvals) {
  if(!STAGES.includes(stage))throw new Error("Choose a production stage.");
  if(STAGES.indexOf(stage)>=4 && !approvals.some(approval=>approvalMatchesDesign(job,approval)))throw new Error("Select Approved in Job overview and save the approval date before advancing production.");
  if(STAGES.indexOf(stage)>=5 && !job.vendorOrderNumber?.trim())throw new Error("Enter the Signs365 order number before marking this job ordered.");
  if(stage==="Complete"){if(!job.shippedDate)throw new Error("Add a ship date under Ordered from Signs365 to complete this job.");validDate(job.shippedDate,true);if(job.shippedDate>today())throw new Error("Ship date cannot be in the future.");if(job.orderDate && job.shippedDate<job.orderDate)throw new Error("Ship date cannot be before the order date.");if(!Number.isInteger(job.finalSalesCents) || job.finalSalesCents<0 || job.finalSalesCents>100000000)throw new Error("Enter the final sales total before completing this job.");}
  return stage;
}

// RFC 4180-style CSV parser: quoted commas, escaped quotes and multiline fields.
export function parseCSV(text) {
  text=String(text).replace(/^\uFEFF/,"");
  if(text.length>2000000)throw new Error("Choose a CSV smaller than 2 MB.");
  const rows=[];let row=[],field="",quoted=false,closed=false;
  for(let i=0;i<text.length;i++) {
    const char=text[i];
    if(quoted){if(char==='"'){if(text[i+1]==='"'){field+='"';i++;}else{quoted=false;closed=true;}}else field+=char;continue;}
    if(char==='"'){if(field || closed)throw new Error("Malformed CSV quote.");quoted=true;continue;}
    if(char==="," || char==="\n" || char==="\r") {
      row.push(field);field="";closed=false;
      if(char!==","){if(row.some(value=>value.trim()))rows.push(row);row=[];if(char==="\r"&&text[i+1]==="\n")i++;}
    }else {if(closed && char.trim())throw new Error("Unexpected text after a quoted field.");if(!closed)field+=char;}
  }
  if(quoted)throw new Error("CSV has an unclosed quote.");
  row.push(field);if(row.some(value=>value.trim()))rows.push(row);
  if(rows.length<2)throw new Error("CSV needs a header and at least one data row.");
  const headers=rows.shift().map(value=>value.trim());
  if(headers.some(value=>!value) || new Set(headers.map(normalize)).size!==headers.length)throw new Error("CSV headers must be present and unique.");
  if(rows.length>300)throw new Error("Import at most 300 rows at a time.");
  if(rows.some(values=>values.length!==headers.length))throw new Error("Some CSV rows have a different number of columns than the header.");
  return {headers,rows};
}
export const IMPORT_FIELDS={name:"Player / team banner name",firstName:"First name",lastName:"Last name",kind:"Banner type (team/senior)",jersey:"Jersey number",graduationYear:"Graduation year",size:"Banner size",quantity:"Quantity",price:"Unit price",photoReference:"Photo reference",payer:"Payer name",orderId:"Order number",notes:"Notes"};
const aliases={name:["name","player name","full name","player / team banner name"],firstName:["first name"],lastName:["last name"],kind:["kind","type","banner type"],jersey:["jersey","jersey number","number"],graduationYear:["graduation year","grad year"],size:["size","banner size"],quantity:["quantity","qty"],price:["price","unit price"],photoReference:["photo reference","image","image number"],payer:["payer","payer name","paid by"],orderId:["order id","order number"],notes:["notes"]};
export const guessMapping = headers => Object.fromEntries(Object.entries(aliases).map(([field,names])=>[field,headers.findIndex(header=>names.includes(normalize(header)))]));
export function previewImport(csv,mapping,existing=[]) {
  const seen=new Set(existing.map(itemKey));
  return csv.rows.map((values,index)=>{
    const input=Object.fromEntries(Object.keys(IMPORT_FIELDS).map(field=>[field,mapping[field]>=0 ? values[mapping[field]] : ""]));
    if(!input.name)input.name=[input.firstName,input.lastName].filter(Boolean).join(" ");
    try {
      if(input.kind && !["team","senior"].includes(normalize(input.kind)))throw new Error("Map banner type to team or senior; edit other product names before import.");
      const item=cleanItem({...input,kind:normalize(input.kind),quantity:input.quantity || 1});
      const key=itemKey(item),duplicate=Boolean(item.name)&&seen.has(key);seen.add(key);
      return {row:index+2,item,duplicate,flags:itemFlags(item),error:""};
    }catch(error){return {row:index+2,item:null,duplicate:false,flags:[],error:error.message};}
  });
}
